import { UserIcon } from '@heroicons/react/16/solid';
import React from 'react';
import { Link } from 'react-router-dom';

import { decodeHtmlEntities } from '@cavela/ui';
import { format } from 'date-fns';
import removeMd from 'remove-markdown';
import { useAuthStore } from 'src/entities/user/store/useAuthStore';
import { TaskStatusTag, UserAvatar } from 'src/shared/components';
import { ROUTE_PATHS } from 'src/shared/config/routes';
import { Reply, Update } from 'src/shared/types/update';

import { Typography } from 'src/shared/components/ui';

import useRouteProductId from '../../hooks/useRouteProductId';
import findUserInOrganization from '../../utils/findUserInOrganization';
import MarkdownContent from './markdown-content/MarkdownContent';

interface ProductUpdatesActionItemProps {
  update: Update;
}

const ProductUpdatesActionItem: React.FC<ProductUpdatesActionItemProps> = ({
  update
}) => {
  const routeProductId = useRouteProductId();
  const renderMessage = (message: string) => {
    const isLongMessage = message?.length > 36;

    if (isLongMessage) {
      return (
        <Typography
          variant="body-1"
          className="text-secondary-text overflow-hidden text-ellipsis whitespace-nowrap truncate"
        >
          {decodeHtmlEntities(removeMd(message))}
        </Typography>
      );
    }

    return (
      <MarkdownContent
        className="text-secondary-text"
        markdownString={message}
      />
    );
  };

  const selectedOrganization = useAuthStore(
    (state) => state.selectedOrganization
  );

  const replyAvatar = (reply: Reply | null) => {
    if (!reply) {
      return undefined;
    }

    const replyUser = reply.user_id
      ? findUserInOrganization(selectedOrganization, reply.user_id)
      : null;

    if (replyUser) {
      return replyUser.avatar_url ?? undefined;
    }

    if (reply.is_staff) {
      return '/images/defaultCavela.svg';
    }
  };

  const renderReplyAvatars = () => {
    if (update?.clarification?.replies?.length > 0) {
      const uniqueReplies = new Map();

      update?.clarification?.replies?.forEach((reply) => {
        if (reply.user_id) {
          uniqueReplies.set(reply.user_id, reply);
        } else if (reply.is_staff && !uniqueReplies.has('staff')) {
          uniqueReplies.set('staff', reply);
        }
      });

      return (
        <div className="flex items-center">
          <div className="flex -space-x-3">
            {Array.from(uniqueReplies.values()).map((reply) => (
              <UserAvatar
                size="sm"
                key={reply.user_id || 'staff'}
                image={replyAvatar(reply)}
                name={reply?.username || 'Cavela staff'}
                disableAnimation
                className="!border-1 !border-white"
              />
            ))}
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <Link
      to={`${ROUTE_PATHS.PRODUCTS}/${routeProductId}?tab=updates&update=${update?.id}`}
      className="flex flex-col p-4 gap-2 rounded-xl border border-gray-3 cursor-pointer hover:bg-gray-1 transition-background"
      key={update?.id}
    >
      <TaskStatusTag
        type={update?.relevant ? 'action-required' : 'in-progress'}
        label={update?.relevant ? 'Action' : 'Resolved'}
      />
      <Typography variant="h6" weight="bold" className="truncate">
        {update?.title}
      </Typography>

      <div>{renderMessage(update?.message)}</div>

      <Typography
        variant="body-1"
        className="text-secondary-text whitespace-nowrap"
      >
        {format(update?.created_at, 'MMM dd, yyyy')}
      </Typography>

      <div className="flex justify-between items-end">
        {update?.cloak && (
          <div className="flex items-center gap-[2.5px]">
            <UserIcon className="size-4 text-secondary-text" />
            <Typography
              variant="body-2"
              className="text-secondary-text whitespace-nowrap"
            >
              {update?.cloak}
            </Typography>
          </div>
        )}

        <div className="flex items-center gap-1">
          {renderReplyAvatars()}
          <Typography
            variant="body-2"
            weight="medium"
            className="text-secondary-text"
          >
            {update?.clarification?.replies?.length ?? 0}{' '}
            {update?.clarification?.replies?.length === 1 ? 'Reply' : 'Replies'}
          </Typography>
        </div>
      </div>
    </Link>
  );
};

export default ProductUpdatesActionItem;
